// Hosted solution

import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PuffLoader from "react-spinners/PuffLoader";
import "./transfer.scss";
import "react-toastify/dist/ReactToastify.css";
import Countdown from "../../components/countdown/countdown";
import { ReactComponent as MoneySvg } from "../../assets/icons/money.svg";
import { ReactComponent as BuildingSvg } from "../../assets/icons/building.svg";
import { ReactComponent as WalletSvg } from "../../assets/icons/wallet.svg";
import { ReactComponent as UserSvg } from "../../assets/icons/user.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";

const publicUrlSandbox = "https://sandbox.touragate.com";
const publicUrlProduction = "https://api.touragate.com";
const publicUrlDev = "https://dev-touragate-d55e1.aws.spicaengine.com/api";

const endpoint = {
  prod: `${publicUrlProduction}/fn-execute/payment/transfer`,
  dev: `${publicUrlDev}/fn-execute/payment/transfer`,
  sandbox: `${publicUrlSandbox}/fn-execute/payment/transfer`,
};

const Transfer = () => {
  const [loading, setLoading] = useState(true);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [transferInfo, setTransferInfo] = useState({ message: "Yükleniyor..." });
  const [iframeSrc, setIframeSrc] = useState();
  const [bankInfo, setBankInfo] = useState();
  const [duration, setDuration] = useState();

  const { txnId, env } = useParams();

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const sessionId = params.get("sessionId");

  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      bankId: "",
    },
  });

  const getTransferData = async () => {
    try {
      const res = await fetch(`${endpoint[env || "sandbox"]}/${txnId}/${sessionId}`);
      const resJson = await res.json();

      if (res.status.toString().startsWith("4")) {
        throw new Error(resJson);
      }

      if (resJson.skipBankScreen) {
        onSubmit({ bankId: undefined });
        return;
      }

      setTransferInfo(resJson);

      if (!resJson.status) {
        toast.error(resJson.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error("Hata! Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const response = await fetch(`${endpoint[env || "sandbox"]}/${txnId}/${sessionId}/approve`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (response.status.toString().startsWith("4")) {
        throw new Error(result);
      }

      if (!result.status) {
        toast.error(result.message);
        setLoading(false);
        return;
      }

      if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
        return;
      }

      const expDate = new Date();
      expDate.setMinutes(expDate.getMinutes() + 15);

      localStorage.setItem("bankInfo", JSON.stringify(result));
      localStorage.setItem("expDate", new Date(expDate).toISOString());

      setDuration(900);
      setBankInfo(result);
    } catch (error) {
      toast.error("Hata! Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const handleClickCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const checkPayment = async () => {
    try {
      setIsSubmiting(true);
      const response = await fetch(`${endpoint[env || "sandbox"]}/${txnId}/${sessionId}/check`);
      const result = await response.json();

      if (response.status === 400 && result.message) {
        toast.error(result.message);
        return;
      }

      localStorage.clear();
      window.location.href = result.url;
    } catch (error) {
      toast.error("Hata! Lütfen tekrar deneyin.");
    } finally {
      setIsSubmiting(false);
    }
  };

  useEffect(() => {
    const _bankInfo = localStorage.getItem("bankInfo");
    const _expDate = localStorage.getItem("expDate");

    let diffSeconds;
    if (_expDate < 1) {
      const diffMs = new Date(_expDate) - new Date();
      diffSeconds = Math.floor(diffMs / 1000);
      localStorage.clear();
    }

    if (!_bankInfo || !diffSeconds) {
      getTransferData();
      return;
    }

    setDuration(Math.max(diffSeconds, 0));
    setBankInfo(JSON.parse(_bankInfo));
    setLoading(false);
  }, []);

  return (
    <section className="transfer-section">
      {loading ? (
        <PuffLoader color="#ff00b4" />
      ) : bankInfo ? (
        <div className="payment-form">
          <div className="form-container">
            {typeof duration === "number" && (
              <div className="countdown">
                <Countdown duration={duration} />
              </div>
            )}

            <div className="form-content">
              <h3>Havale Bilgileri</h3>
              <div className="sub-title">
                Lütfen {bankInfo.amount} {bankInfo.currency} tutarını aşağıdaki hesaba havale edin. Havale işlemi tamamlandıktan sonra "Havale Yapıldı" butonuna tıklayarak işlemi tamamlayabilirsiniz.
              </div>
              <div className="warning">
                Sayaç dolduğunda işleminiz iptal edilecektir, lütfen zamanı dikkate alın.
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group">
                  <label>
                    <UserSvg /> Hesap Adı
                  </label>
                  <input readOnly value={bankInfo.bank.receiver} />
                  <CopySvg
                    className="copy"
                    onClick={() => handleClickCopy(bankInfo.bank.receiver)}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <WalletSvg /> IBAN
                  </label>
                  <input readOnly value={bankInfo.bank.iban} />
                  <CopySvg className="copy" onClick={() => handleClickCopy(bankInfo.bank.iban)} />
                </div>
                <div className="form-group">
                  <label>
                    <MoneySvg /> Tutar
                  </label>
                  <input readOnly value={`${bankInfo.amount} ${bankInfo.currency}`} />
                </div>
                <button
                  type="button"
                  className="pay-button"
                  onClick={checkPayment}
                  disabled={isSubmiting}
                >
                  {isSubmiting ? <PuffLoader color="#fff" size={20} /> : `Havale Yapıldı`}
                </button>
              </form>
            </div>
          </div>
        </div>
      ) : iframeSrc ? (
        <div className="iframe-container">
          <iframe title="payment-iframe" src={iframeSrc} frameBorder="0" width="100%" height="100%"></iframe>
        </div>
      ) :
        transferInfo?.status ? (
          <div className="payment-form">
            <div className="form-container">
              <div className="form-content">
                <h3>Havale İşlemi</h3>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <label>
                      <UserSvg /> Ad Soyad
                    </label>
                    <input readOnly value={transferInfo.fullName} />
                  </div>
                  <div className="form-group">
                    <label>
                      <MoneySvg /> Tutar
                    </label>
                    <input readOnly value={`${transferInfo.amount} ${transferInfo.currency}`} />
                  </div>
                  <div className="form-group">
                    <label>
                      <BuildingSvg /> Banka
                    </label>
                    <select
                      placeholder="Banka Seçin"
                      value={watch("bankId")}
                      {...register("bankId", {
                        required: true,
                        validate: (value) => value !== "",
                      })}
                    >
                      <option value="" disabled>
                        Banka Seçin
                      </option>
                      {transferInfo?.banks?.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="pay-button"
                    disabled={!watch("bankId").length || isSubmiting}
                  >
                    Devam Et
                  </button>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="errorSection">{transferInfo.message}</div>
        )}
    </section>
  );
};

export default Transfer;
